/* Copy da tela de registro de release por descoberta (ADR-0704). O alerta de "nenhuma versão" e
   a dica de versão semver embutem um trecho de código inline (`<code>`); o texto ao redor vira
   campo próprio para a marcação continuar no componente e só o texto vir da copy. */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface ReleaseRegisterContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly breadcrumbAriaLabel: string;
  readonly breadcrumbBack: string;
  readonly noPermission: string;
  readonly originSectionTitle: string;
  readonly channelLabel: string;
  readonly versionLabel: string;
  readonly versionsInBucketLabel: string;
  readonly noVersionsIntro: string;
  /** Nome do placeholder dentro de `<code>canal/&lt;placeholder&gt;/</code>`. */
  readonly noVersionsPathPlaceholder: string;
  readonly noVersionsOutro: string;
  readonly artifactsSectionTitle: string;
  readonly selectVersionHintIntro: string;
  readonly selectVersionHintOutro: string;
  readonly emptyArtifactsTitle: string;
  readonly emptyArtifactsDescription: string;
  readonly table: {
    readonly platform: string;
    readonly type: string;
    readonly format: string;
    readonly file: string;
    readonly size: string;
    readonly sha512: string;
    readonly status: string;
  };
  readonly artifactKindInstaller: string;
  readonly artifactKindUpdate: string;
  readonly artifactAlreadyRegistered: string;
  readonly artifactNew: string;
  readonly releaseNotesLabel: string;
  readonly releaseNotesPlaceholder: string;
  readonly notesSyncHint: string;
  readonly cancelButton: string;
  readonly nothingToRegister: string;
  readonly syncNotesButton: string;
  readonly registerCount: (count: number) => string;
  readonly toastNotesSynced: string;
  readonly toastRegisteredWithSkipped: (registered: number, skipped: number) => string;
  readonly toastRegisteredAllNew: (registered: number) => string;
}

const RELEASE_REGISTER_CONTENT: Bilingual<ReleaseRegisterContent> = {
  'pt-BR': {
    pageTitle: 'Registrar release',
    pageDescription:
      'Escolha o canal e a versão publicada no bucket. Os metadados (plataforma, arquitetura, ' +
      'sha512 e tamanho) são lidos automaticamente dos manifestos do build.',
    breadcrumbAriaLabel: 'Trilha',
    breadcrumbBack: 'Atualizações',
    noPermission: 'Você não tem permissão para registrar releases.',
    originSectionTitle: 'Origem no bucket',
    channelLabel: 'Canal',
    versionLabel: 'Versão',
    versionsInBucketLabel: 'Versões no bucket',
    noVersionsIntro: 'Nenhuma versão listada no bucket para este canal (o CI publica em ',
    noVersionsPathPlaceholder: 'versão',
    noVersionsOutro: '). Informe a versão manualmente acima.',
    artifactsSectionTitle: 'Artefatos descobertos',
    selectVersionHintIntro:
      'Selecione uma versão do bucket ou informe uma versão semver válida (ex.: ',
    selectVersionHintOutro: ') para descobrir os artefatos.',
    emptyArtifactsTitle: 'Nenhum artefato encontrado',
    emptyArtifactsDescription: 'O bucket não tem artefatos distribuíveis para essa versão e canal.',
    table: {
      platform: 'Plataforma',
      type: 'Tipo',
      format: 'Formato',
      file: 'Arquivo',
      size: 'Tamanho',
      sha512: 'sha512',
      status: 'Estado',
    },
    artifactKindInstaller: 'instalador',
    artifactKindUpdate: 'atualização',
    artifactAlreadyRegistered: 'registrada',
    artifactNew: 'nova',
    releaseNotesLabel: 'Notas / changelog (markdown, opcional)',
    releaseNotesPlaceholder: '## Novidades\n- ...',
    notesSyncHint:
      'Todos os artefatos já estão registrados. Esta ação atualiza somente as notas canônicas ' +
      'da versão; o estado e a promoção não mudam.',
    cancelButton: 'Cancelar',
    nothingToRegister: 'Nada a registrar',
    syncNotesButton: 'Salvar notas da versão',
    registerCount: (count) => `Registrar ${count} artefato(s)`,
    toastNotesSynced: 'Notas da versão atualizadas.',
    toastRegisteredWithSkipped: (registered, skipped) =>
      `${registered} registrada(s), ${skipped} já existia(m).`,
    toastRegisteredAllNew: (registered) => `${registered} release(s) registrada(s) (rascunho).`,
  },
  'en-US': {
    pageTitle: 'Register release',
    pageDescription:
      'Choose the channel and the version published to the bucket. Metadata (platform, ' +
      'architecture, sha512, and size) is read automatically from the build manifests.',
    breadcrumbAriaLabel: 'Breadcrumb',
    breadcrumbBack: 'Updates',
    noPermission: 'You do not have permission to register releases.',
    originSectionTitle: 'Bucket origin',
    channelLabel: 'Channel',
    versionLabel: 'Version',
    versionsInBucketLabel: 'Versions in bucket',
    noVersionsIntro: 'No version listed in the bucket for this channel (CI publishes to ',
    noVersionsPathPlaceholder: 'version',
    noVersionsOutro: '). Enter the version manually above.',
    artifactsSectionTitle: 'Discovered artifacts',
    selectVersionHintIntro:
      'Select a version from the bucket or enter a valid semver version (e.g., ',
    selectVersionHintOutro: ') to discover artifacts.',
    emptyArtifactsTitle: 'No artifact found',
    emptyArtifactsDescription:
      'The bucket has no distributable artifacts for this version and channel.',
    table: {
      platform: 'Platform',
      type: 'Type',
      format: 'Format',
      file: 'File',
      size: 'Size',
      sha512: 'sha512',
      status: 'Status',
    },
    artifactKindInstaller: 'installer',
    artifactKindUpdate: 'update',
    artifactAlreadyRegistered: 'registered',
    artifactNew: 'new',
    releaseNotesLabel: 'Notes / changelog (markdown, optional)',
    releaseNotesPlaceholder: "## What's new\n- ...",
    notesSyncHint:
      'All artifacts are already registered. This updates only the canonical release notes; ' +
      'status and promotion remain unchanged.',
    cancelButton: 'Cancel',
    nothingToRegister: 'Nothing to register',
    syncNotesButton: 'Save version notes',
    registerCount: (count) => `Register ${count} artifact(s)`,
    toastNotesSynced: 'Version notes updated.',
    toastRegisteredWithSkipped: (registered, skipped) =>
      `${registered} registered, ${skipped} already existed.`,
    toastRegisteredAllNew: (registered) => `${registered} release(s) registered (draft).`,
  },
};

export const useReleaseRegisterContent = (): ReleaseRegisterContent =>
  useBilingual(RELEASE_REGISTER_CONTENT);
